import { DriveStep } from 'driver.js';

import { TourStopButtonAction } from './tour-stop-button-action';
import { TourStopKind } from './tour-stop-kind';
import { TourStopPosition } from './tour-stop-position';

/**
 * Abstract base class for all tour stops.
 *
 * This class defines the common interface and properties that all tour stops
 * must implement. Tour stops represent individual steps in a tour itinerary
 * and are responsible for rendering their content and handling user interactions.
 *
 * @abstract
 * @example
 * ```typescript
 * class CustomTourStop extends TourStop {
 *   toDriverJsStep(): DriveStep {
 *     return {
 *       element: '#my-element',
 *       popover: {
 *         title: 'Custom Stop',
 *         description: 'This is a custom tour stop'
 *       }
 *     };
 *   }
 * }
 * ```
 */
export abstract class TourStop {
  /** The type of tour stop - determines rendering behavior */
  kind: TourStopKind = 'custom';

  /**
   * Determines whether this tour stop can be activated.
   *
   * This method can be overridden by subclasses to implement custom
   * activation logic, such as checking if required DOM elements exist
   * or if certain conditions are met.
   *
   * @returns True if the stop can be activated, false otherwise
   *
   * @example
   * ```typescript
   * canActivate() {
   *   return document.querySelector('#required-element') !== null;
   * }
   * ```
   */
  canActivate() {
    return true;
  }

  /**
   * Display text values for template interpolation.
   * Keys in this object can be referenced in templates using {{key}} syntax.
   */
  displayText: Record<string, string> = {};

  /**
   * Button actions that can be triggered from the tour stop UI.
   * Keys correspond to data-tour-action-key attributes in templates.
   */
  buttonActions: Record<string, TourStopButtonAction> = {};

  /** Optional positioning configuration for the tour stop popover */
  position?: TourStopPosition;

  /**
   * Converts this tour stop to a driver.js step configuration.
   *
   * This abstract method must be implemented by all subclasses to define
   * how the tour stop should be rendered by the driver.js library.
   *
   * @returns A driver.js step configuration object
   *
   * @example
   * ```typescript
   * toDriverJsStep(): DriveStep {
   *   return {
   *     element: this.targetElement,
   *     popover: {
   *       title: this.title,
   *       description: this.description
   *     }
   *   };
   * }
   * ```
   */
  abstract toDriverJsStep(): DriveStep;
}
